import React, { MouseEvent, useState } from 'react'
import { useAppDispatch } from '../../../hooks/useAppDispatch'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { selectProductBackupSettingsDefault } from '../../../store/selectors/settingsSelectors'
import { getBackupSettingsValidationSchema } from '../utils/getBackupSettingsValidationSchema'
import { useFormValidation } from '../../../hooks/useFormValidation'
import * as yup from 'yup'
import { updateProductBackupSettings } from '../../../store/async-thunks/settings/updateProductBackupSettings'
import { settingsActions } from '../../../store/slices/settingsSlice'
import { getBackupSettingsControlDescriptors } from '../utils/getBackupSettingsControlDescriptors'
import { buildControlsExt } from '../../inputs/controls'
import { useEffectAfterMount } from '../../../hooks/useEffectAfterMount'
import { modifyBackupSettings } from '../../../store/actions/coreSettingsActions'
import _isEqual from 'lodash/isEqual'

interface Props {
    guid: string
    backupSettings: any
}

export const BackupSettingsFields = ({ guid, backupSettings }: Props) => {
    const dispatch = useAppDispatch()
    const [isApplyDisabled, setIsApplyDisabled] = useState(false)
    const backupSettingsDefault = useAppSelector(
        selectProductBackupSettingsDefault(guid)
    )

    const validationSchema = getBackupSettingsValidationSchema()
    const [state, setState, touched, setTouched, validationResult] =
        useFormValidation(backupSettings, validationSchema)

    const checkSchema = yup.object().shape(validationSchema)
    const isFormValid = checkSchema.isValidSync(state)

    const isTouched = Object.keys(touched).length
    const isSame = _isEqual(backupSettingsDefault, state)

    const handleApply = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        setIsApplyDisabled(true)
        dispatch(updateProductBackupSettings({ guid }))
        setTimeout(() => setIsApplyDisabled(false), 3000)
    }

    const handleReset = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        dispatch(settingsActions.resetBackupSettings(guid))
        setState(backupSettingsDefault)
    }

    const controlDescriptors = getBackupSettingsControlDescriptors(state)
    const controlsArr = buildControlsExt(
        controlDescriptors,
        state,
        setState,
        touched,
        setTouched,
        validationResult
    )

    useEffectAfterMount(() => {
        dispatch(modifyBackupSettings({ guid, data: state }))
    }, [state])

    useEffectAfterMount(() => {
        setIsApplyDisabled(!isTouched)
    }, [isTouched])

    return (
        <div className="d-flex flex-column gap-4 px-2 pt-4">
            <div className="d-flex flex-column gap-2">
                {!!controlsArr.length &&
                    controlsArr.map((el, i) => (
                        <div
                            key={i}
                            className={
                                el.key !== 'is_backup_enabled' &&
                                !state?.is_backup_enabled
                                    ? 'opacity-50 viewOnly'
                                    : ''
                            }
                        >
                            {el}
                        </div>
                    ))}
            </div>
            <div className="d-flex gap-2">
                <button
                    type="button"
                    onClick={handleReset}
                    className="app-btn-main app-btn-apply fit-content mt-2"
                    disabled={isSame}
                >
                    Reset
                </button>
                <button
                    type="button"
                    onClick={handleApply}
                    className="app-btn-main app-btn-reset fit-content mt-2"
                    disabled={isSame || isApplyDisabled || !isTouched || !isFormValid}
                >
                    Apply
                </button>
            </div>
        </div>
    )
}
